/* verqo — Seller Product page content (12 pages)
   All pages follow the catalog pattern (Feb 2026):
     Hero (single CTA + heroVisual browser-frame)
     Feature grid (rich, 6-8 items per user brief)
     Screenshot showcase (real portal captures, tabbed where multiple views)
     AltBlocks with visual screenshots
     Quote / FAQ / Related / CTA
   Screenshots live in /app/frontend/public/preview-screens/portal-*
   No "Book a demo" CTAs — every page uses "Get started" + optional
   "See how it works" placed BELOW the screenshot showcase (per user brief).
*/
import {
  Package, Store, MessageSquare, Boxes, Banknote, Landmark, Receipt,
  Truck, AlertTriangle, TrendingUp, Shield,
  Zap, Sparkles, Building2, Clock, Layers, Lock, Wallet, DollarSign,
  LayoutGrid, Wand2, FileSpreadsheet, Rocket, Plug, Users2, RefreshCw,
  ShieldCheck, Award, Bell, Bot, Search, LinkIcon, Gauge, PieChart,
  CheckCircle2, CircleAlert, Zap as Bolt, Percent, ChartBar,
} from "lucide-react";
import { BrowserFrame } from "../../../../components/marketing/SubPageBlocks";

const CTA_PRIMARY = { label: "Get started", href: "/waitlist?as=seller" };

const SHOWCASE_CTA = {
  primary:   { label: "Get started",      href: "/waitlist?as=seller" },
  secondary: { label: "See how it works", href: "/company/how-it-works" },
};

const commonCta = {
  title: "Ship your first invoice in 48 hours.",
  subtitle: "Import your buyer list, upload a catalog, and start moving cash the same day. No implementation fees, no long contracts.",
  primary: CTA_PRIMARY,
  secondary: { label: "Talk to a founder", href: "/company/contact" },
};

const REL = (slug, label, desc) => ({ href: `/product/${slug}`, label, desc });

/* Screenshot paths — organized by portal area */
const IMG = {
  catalogGrid:   "/preview-screens/portal-catalog/catalog-grid.jpeg",
  catalogList:   "/preview-screens/portal-catalog/catalog-list.jpeg",
  catalogText:   "/preview-screens/portal-catalog/catalog-text.jpeg",
  catalogAdmin:  "/preview-screens/portal-catalog/catalog-admin.jpeg",
  dashboard:     "/preview-screens/portal-dashboard/dashboard.jpeg",
  ordersAll:     "/preview-screens/portal-orders/all.jpeg",
  ordersAction:  "/preview-screens/portal-orders/action.jpeg",
  ordersProg:    "/preview-screens/portal-orders/progress.jpeg",
  ordersClaims:  "/preview-screens/portal-orders/claims.jpeg",
  ordersDropship:"/preview-screens/portal-orders/dropship.jpeg",
  rfqMain:       "/preview-screens/portal-rfq/main.jpeg",
  buyersMain:    "/preview-screens/portal-buyers/main.jpeg",
  walletOverview:"/preview-screens/portal-wallet/overview.jpeg",
  walletWallet:  "/preview-screens/portal-wallet/wallet.jpeg",
  walletCredit:  "/preview-screens/portal-wallet/credit.jpeg",
  walletMethods: "/preview-screens/portal-wallet/methods.jpeg",
  walletActivity:"/preview-screens/portal-wallet/activity.jpeg",
  invoicesMain:  "/preview-screens/portal-invoices/main.jpeg",
  shippingMain:  "/preview-screens/portal-shipping/main.jpeg",
  insuranceMain: "/preview-screens/portal-insurance/main.jpeg",
  dropshipMain:  "/preview-screens/portal-dropship/main.jpeg",
};

const URL = (path) => `app.verqo.com${path}`;

/* -------- 1. CATALOG (already refined — kept) -------- */
const catalog = {
  eyebrow: "Product · Sell",
  title: "One catalog. Every buyer sees the right price.",
  subtitle: "Manage every SKU, MOQ, pricing tier, and payment term in one place. Buyers see their custom price the second they log in — no more spreadsheet PDFs, no more back-and-forth on email.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.catalogGrid} alt="Verqo catalog — buyer-facing grid view" url="sellers.verqo.com/s/verqo-demo-supply-co/catalog" />,
  features: {
    heading: "Everything wholesale should have been from day one.",
    sub: "A modern catalog engine built for the way brands actually sell into retail — with tiered pricing, MOQs, case-packs, and buyer-specific rules baked in from the ground up.",
    items: [
      { icon: RefreshCw,       title: "Live, always-up-to-date",  desc: "Change a price, stock level, or promo once — it propagates across storefront, order forms, and open POs in under a second." },
      { icon: Wand2,           title: "AI import (any format)",   desc: "Drop in a supplier linesheet, Shopify export, PDF price book, or scan of a distributor deck — our AI maps SKUs, prices, tiers, and images automatically." },
      { icon: FileSpreadsheet, title: "One-click CSV import",     desc: "Bulk-upload thousands of SKUs from Excel or Google Sheets. Column mapping remembers itself for future imports." },
      { icon: Rocket,          title: "Guided setup wizard",       desc: "Pick your industry, pick your template, and the catalog scaffolds itself — categories, price tiers, and MOQ rules pre-configured for beauty, apparel, food, home, health, and CPG." },
      { icon: Plug,            title: "Buyer API",                 desc: "Enterprise buyers pull your live catalog into their PIM, ERP, or e-com platform via a signed REST + webhook API. Every change syncs in real time." },
      { icon: Users2,          title: "Buyer-specific catalogs",   desc: "Each buyer sees a curated assortment with their own price tier, MOQs, terms, and promotions. Rules-based, not memory-based." },
      { icon: DollarSign,      title: "Per-buyer tier pricing",    desc: "Gold/Silver/Bronze tiers, seasonal price windows, volume discounts, or per-buyer overrides — enforced automatically at checkout." },
      { icon: Layers,          title: "MOQ, case-pack, bundles",   desc: "Enforce minimums by SKU, brand, or order total. Sell singles, cases, or bundles — no more manual clean-up on the invoice." },
    ],
  },
  screenshots: {
    heading: "Every buyer, three ways to browse.",
    sub: "The same catalog rendered for the way a specific buyer likes to work — a merchandiser wants pictures, a rep wants a spreadsheet, an ops buyer wants a quick-order text list. All three, out of the box, no config.",
    items: [
      { label: "Grid view", src: IMG.catalogGrid, alt: "Verqo catalog — grid view",  url: "sellers.verqo.com/s/…/catalog", caption: "Grid view — visual-first for merchandisers and store managers. Product hero, promo badges (Top seller, Weekly special, New), and per-unit or per-case pricing." },
      { label: "List view", src: IMG.catalogList, alt: "Verqo catalog — list view",  url: "sellers.verqo.com/s/…/catalog", caption: "List view — spreadsheet-fast for reps and pro buyers. Every row shows SKU, MOQ, stock, and retail-vs-wholesale side-by-side." },
      { label: "Text view", src: IMG.catalogText, alt: "Verqo catalog — text view",  url: "sellers.verqo.com/s/…/catalog", caption: "Text view — quick-order for buyers who know exactly what they want. SKU-first table with paste-to-order support." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "From messy to source-of-truth",
      title: "Kill the linesheet PDF. Keep the workflow.",
      body: "Every rep, every buyer, and every integration reads from one catalog. Change a price once and it propagates across your storefront, order forms, EDI 852s, and open POs — no CSV shuffle.",
      bullets: ["Bulk edit by category or tag", "Version history + one-click rollback", "Rep-friendly quick-add cart"],
      cta: { label: "See the storefront", href: "/product/storefront" },
      visual: <BrowserFrame src={IMG.catalogAdmin} alt="Verqo seller-portal catalog admin — 500 SKUs, categories, pricing" url={URL("/seller/catalog")} />,
    },
    {
      eyebrow: "Buyer-specific pricing at scale",
      title: "Every buyer, their own front door.",
      body: "New buyers land on a curated catalog with the right assortment, price tier, and payment terms — set by rules, not by rep memory. Rules can be based on business type, region, order history, or trade score.",
      bullets: ["Auto-tier assignment on approval", "Bundle & promo pricing windows", "Hide out-of-region SKUs automatically"],
      visual: <BrowserFrame src={IMG.catalogGrid} alt="Verqo buyer front-door — personalized catalog with tier-specific pricing" url="sellers.verqo.com/s/verqo-demo-supply-co/catalog" />,
    },
  ],
  quote: {
    quote: "We used to email a 400-page PDF linesheet. Verqo made it feel like buyers were shopping our real store, at their real price.",
    author: "Amelia R.", role: "Head of Wholesale", company: "an indie beauty brand",
  },
  faq: [
    { q: "Can I import from Shopify or Faire?", a: "Yes — one-click imports from Shopify, WooCommerce, Faire CSV, Amazon Seller Central, and QuickBooks. Ongoing sync keeps your catalog current — you only edit in one place." },
    { q: "Do I need to change my SKUs?",         a: "No. Verqo keeps your existing SKUs. Barcode, UPC, GTIN, and internal codes are all preserved and searchable." },
    { q: "What about wholesale-only items?",     a: "Mark SKUs as wholesale-only and they'll never appear on your DTC storefront, but stay available to approved retailers via the wholesale front door." },
    { q: "How does the AI import work?",         a: "Drop in ANY format — PDF linesheet, Excel export, screenshot, or supplier deck. Our AI reads SKUs, prices, MOQs, images, and category tags automatically. You review, we save. Under 2 minutes for 1000 SKUs." },
    { q: "Can our largest buyers pull our catalog via API?", a: "Yes. The Verqo Buyer API returns your live catalog as JSON with signed URLs for product images, real-time stock, and per-buyer pricing. Full webhook support for change events." },
  ],
  related: [
    REL("storefront", "Storefront",  "Ship a white-label B2B store in minutes."),
    REL("rfq",        "RFQ",         "Handle big custom quotes without email ping-pong."),
    REL("payments",   "Payments",    "Get paid instantly on every invoice."),
  ],
  cta: commonCta,
};

/* -------- 2. STOREFRONT -------- */
const storefront = {
  eyebrow: "Product · Sell",
  title: "Your wholesale storefront, live in an afternoon.",
  subtitle: "A white-label B2B storefront with your brand, your domain, your rules. Retailers log in, reorder, and settle in one place. No developers required, no six-month rebuild.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.catalogList} alt="Verqo storefront — a live white-label B2B store" url="sellers.verqo.com/s/verqo-demo-supply-co/catalog" />,
  features: {
    heading: "Everything a wholesale buyer expects — nothing they don't.",
    sub: "Ship a real B2B storefront in an afternoon. Not a Shopify plugin, not a rescaled DTC theme — a native B2B experience your retailers will actually use.",
    items: [
      { icon: Store,       title: "White-label branding",   desc: "Custom domain, colors, typography, and logo — no verqo branding, no plug marks." },
      { icon: Layers,      title: "Curated collections",    desc: "Feature seasonal drops, best-sellers, and buyer-specific bundles — merchandise like it's DTC." },
      { icon: Bolt,        title: "Fast quick-order",       desc: "SKU paste, CSV upload, and saved lists for pro buyers — reorder 300 SKUs in 30 seconds." },
      { icon: Landmark,    title: "Terms at checkout",      desc: "Buyers pick pay-now, ACH, card, or Net-30/60/90 at checkout — no back-and-forth email." },
      { icon: Users2,      title: "Rep-facing tools",       desc: "Reps see their book, their pipeline, and place orders on behalf of buyers — all from the same storefront." },
      { icon: Sparkles,    title: "Native mobile",          desc: "Ninety percent of B2B reorders start on mobile. Your storefront is a proper responsive app — not a shrunk desktop." },
    ],
  },
  screenshots: {
    heading: "Real production storefronts — running on Verqo today.",
    sub: "Every storefront you see is a live, custom-branded B2B store powered by Verqo. Your buyers land on your brand — not ours.",
    items: [
      { label: "Storefront home", src: IMG.catalogGrid, alt: "Live Verqo storefront — grid view",   url: "sellers.verqo.com/s/verqo-demo-supply-co/catalog", caption: "Grid-first browsing — merchandise-forward layout with real product hero images, tier pricing, and promo badges." },
      { label: "Pro buyer",       src: IMG.catalogList, alt: "Live Verqo storefront — list view",   url: "sellers.verqo.com/s/verqo-demo-supply-co/catalog", caption: "Spreadsheet-mode for pro buyers — every SKU in one row, MOQ + stock + price at a glance." },
      { label: "Quick order",     src: IMG.catalogText, alt: "Live Verqo storefront — text view",   url: "sellers.verqo.com/s/verqo-demo-supply-co/catalog", caption: "Paste-in reorder for ops buyers who know their SKUs — under 60 seconds to submit a 500-line PO." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "Buyers who show up alone",
      title: "Retailers reorder themselves. You keep the margin.",
      body: "Most reps waste half their week on repeat orders. With the Verqo storefront, retailers pull open their saved list, tap reorder, and settle — no phone call, no email chain, no rep tax on the order.",
      bullets: ["Saved lists, favorites, last-order recall", "One-click reorder with new pricing applied", "Reps freed up to open net-new accounts"],
      visual: <BrowserFrame src={IMG.dashboard} alt="Seller portal dashboard — order & revenue analytics" url={URL("/seller")} />,
    },
    {
      eyebrow: "Bring your own domain",
      title: "wholesale.yourbrand.com, up in an hour.",
      body: "Point a subdomain at Verqo and we handle SSL, redirects, and DNS. Every page — catalog, product detail, checkout, order tracking — carries your brand.",
      bullets: ["Custom fonts, colors, and lockup", "SEO-friendly URLs", "Auto SSL, no cert renewals"],
      visual: <BrowserFrame src={IMG.catalogList} alt="White-label storefront — list view under a custom domain" url="wholesale.your-brand.com/catalog" />,
    },
  ],
  quote: {
    quote: "Our retailers used to place orders via text message. Now they log in, reorder, and pay — I get my Sundays back.",
    author: "Ravi S.", role: "Founder", company: "a specialty coffee brand",
  },
  faq: [
    { q: "Can I use my own domain?",     a: "Yes. Point wholesale.yourbrand.com at Verqo and we handle SSL automatically — no cert renewals to remember." },
    { q: "Is it responsive?",             a: "It's mobile-first. Ninety percent of buyer reorders start on mobile — the Verqo storefront handles all of them as a native app-feeling experience." },
    { q: "Can I keep some SKUs private?", a: "Yes. Set SKUs to be visible only to approved retailers, specific tiers, or specific regions." },
    { q: "Do I need any developers?",     a: "No. The visual editor + guided setup wizard gets you live in an hour. If you want deeper customization, our Buyer API supports headless integrations." },
  ],
  related: [
    REL("catalog",  "Catalog",  "One catalog powers your storefront, reps, and integrations."),
    REL("rfq",      "RFQ",      "Handle bespoke quotes when a buyer needs off-catalog pricing."),
    REL("payments", "Payments", "Accept ACH, card, wire — with one reconciliation."),
  ],
  cta: commonCta,
};

/* -------- 3. RFQ -------- */
const rfq = {
  eyebrow: "Product · Sell",
  title: "Big orders shouldn't need 40 emails.",
  subtitle: "RFQ turns messy back-and-forth into a structured quote flow: buyer asks, you scope, both sides sign — in one thread, one record, one invoice. No spreadsheet chasing, no lost quotes.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.rfqMain} alt="Verqo RFQ workspace" url={URL("/seller/rfq")} />,
  features: {
    heading: "Stop losing quotes in your inbox.",
    sub: "A structured quote pipeline replacing 40-email threads — with signature capture, version history, and one-click convert-to-order.",
    items: [
      { icon: MessageSquare, title: "Structured quote forms",    desc: "Buyers submit exactly the details you need — SKUs, quantities, ship-to, terms, target date. No missing fields, no clarifying emails." },
      { icon: Clock,         title: "SLA tracking",              desc: "See which quotes are aging. Reps get pinged before a deal goes cold, and buyers get an ETA when they submit." },
      { icon: Bolt,          title: "One-click to invoice",      desc: "When both sides sign, the quote becomes a live order — no re-entry, no copy-paste, no risk of price drift." },
      { icon: Layers,        title: "Version history",           desc: "Every counter-proposal is captured. No more \"wait, which price did we agree on?\"" },
      { icon: ShieldCheck,   title: "E-signature built-in",      desc: "Signed by both parties inside the platform — audit trail, timestamps, IP-address record for every signature event." },
      { icon: DollarSign,    title: "Payment terms at accept",   desc: "Extend Net-30/60/90 or Verqo Advance directly on the accepted quote — no separate credit application." },
    ],
  },
  blocks: [
    {
      eyebrow: "For custom orders",
      title: "Buyers send one link. You send one price.",
      body: "Custom mixes, private label, seasonal bundles — RFQ handles it all with a shareable link buyers can fill out from mobile in 90 seconds. Reps never touch email again.",
      bullets: ["Public RFQ link — no login required", "Auto-router to the right rep", "Prefilled buyer info + shipping address"],
      visual: <BrowserFrame src={IMG.ordersAction} alt="Orders — action queue with pending quotes" url={URL("/seller/orders#action")} />,
    },
    {
      eyebrow: "Sales cycles you can measure",
      title: "Every quote tracked, every win logged.",
      body: "Verqo gives you a real pipeline view — open, quoted, won, lost — so you know which SKUs and reps are actually closing.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Seller dashboard — quote pipeline analytics" url={URL("/seller")} />,
    },
  ],
  faq: [
    { q: "Can buyers accept via email?",       a: "Yes. Buyers can review and accept a quote from the emailed link — no login required for one-off deals." },
    { q: "Does it integrate with QuickBooks?", a: "Signed quotes push straight to QuickBooks or Xero as invoices with full line-item detail — no manual entry." },
    { q: "Can I bulk-import RFQs?",             a: "Yes. Upload a CSV of open RFQs from your inbox and Verqo routes them into the pipeline with SLA counters started." },
  ],
  related: [
    REL("catalog",     "Catalog",     "For standard reorders, use the storefront."),
    REL("payments",    "Payments",    "Get paid on the RFQ once it's signed."),
    REL("credit-terms","Credit terms","Extend Net-30/60/90 on custom quotes."),
  ],
  cta: commonCta,
};

/* -------- 4. DROPSHIP -------- */
const dropship = {
  eyebrow: "Product · Sell",
  title: "Dropship for retailers, without the ops nightmare.",
  subtitle: "Route buyer orders straight to your 3PL. Verqo handles labels, tracking, claims, returns, and invoicing — retailers never see your inventory system, and your ops team never touches a shipping tool.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dropshipMain} alt="Verqo Dropship Automation — fulfillment center" url={URL("/seller/dropship")} />,
  features: {
    heading: "The channel-agnostic dropship OS.",
    sub: "Everything a retailer needs to sell your full SKU catalog online — without holding a single unit of your inventory. Freight, parcel, claims, invoicing, EDI — all under one roof.",
    items: [
      { icon: Boxes,       title: "Order routing",             desc: "Route to any 3PL, in-house warehouse, or brand-direct fulfillment based on rules per buyer, SKU, or region." },
      { icon: Truck,       title: "Discounted labels",         desc: "Multi-carrier rates from USPS, UPS, FedEx, DHL, and dozens of LTL freight carriers — no minimums." },
      { icon: AlertTriangle,title: "Auto-filed claims",        desc: "Damaged in transit? We open the claim the moment the buyer flags it — with photos, tracking, and carrier communication threaded automatically." },
      { icon: Sparkles,    title: "Retailer-branded packing",  desc: "Slip and label carry the retailer's brand, not yours. Custom inserts and hand-note printing supported." },
      { icon: Bot,         title: "Auto-invoices",             desc: "Invoice generated the moment the truck rolls. Buyer sees it in their portal, terms attached, pay-now button live." },
      { icon: Plug,        title: "EDI 850 / 855 / 856 / 810", desc: "Full EDI support for enterprise retailers who require it — with human-readable audit logs for every message." },
      { icon: LinkIcon,    title: "Shopify / WooCommerce sync",desc: "Auto-syncs orders and tracking back to the retailer's e-com platform. No CSV exports, no missed shipments." },
      { icon: ChartBar,    title: "P&L per retailer",           desc: "Live dashboard showing dropship revenue, cost, and margin per retailer — with alerts when SLAs slip." },
    ],
  },
  screenshots: {
    heading: "Every corner of dropship, live.",
    sub: "Fulfillment center, catalog sync, auto-invoicing, claims, terms, analytics — one app for the entire channel-agnostic dropship flow.",
    items: [
      { label: "Fulfillment", src: IMG.dropshipMain,   alt: "Dropship — Fulfillment Center with 380 orders across 2 retailers", url: URL("/seller/dropship"),        caption: "Fulfillment Center — 380 orders across two connected retailers, with queue filters, print-labels, pick-lists, and end-of-day cutoff automation." },
      { label: "Orders",      src: IMG.ordersDropship, alt: "Dropship orders queue",                                             url: URL("/seller/orders#dropship"), caption: "Dropship orders — routed through the Verqo unified orders queue, sitting alongside your DTC and wholesale flows with the same shipping and claims tooling." },
      { label: "Claims",      src: IMG.ordersClaims,   alt: "Dropship claims — auto-filed with carriers",                        url: URL("/seller/orders#claims"),   caption: "Claims — auto-filed the moment a buyer flags damage. Photo capture, carrier communication, and payout tracked in one thread." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "For brands with retail partners",
      title: "Your best retailers can offer your full catalog online — without carrying inventory.",
      body: "Dropship unlocks the long-tail SKUs your retailers can't afford to stock. You ship, they earn margin, everyone wins.",
      visual: <BrowserFrame src={IMG.ordersAll} alt="Unified orders — 910 orders across every channel in one view" url={URL("/seller/orders")} />,
    },
    {
      eyebrow: "Zero double-entry",
      title: "One order, one label, one truth.",
      body: "Orders sync to Verqo and to your 3PL simultaneously. No CSV exports, no manual pushes, no missed shipments.",
      visual: <BrowserFrame src={IMG.invoicesMain} alt="Invoices generated automatically from dropship orders" url={URL("/seller/invoices")} />,
    },
  ],
  faq: [
    { q: "Which 3PLs are supported?",   a: "ShipBob, Deliverr, ShipHero, ShipMonk, Amazon FBA, plus any warehouse using WMS with an API. Custom integrations in 5 business days." },
    { q: "How do returns work?",        a: "Verqo generates a prepaid return label routed back to the warehouse. Automatic buyer refund once received." },
    { q: "Is EDI included?",             a: "Yes. EDI 850/810/855/856 with major retailers (Target, Walmart, Nordstrom, etc.) is included at every plan tier. Custom trading partners on request." },
  ],
  related: [
    REL("shipping",  "Shipping",  "Discounted parcel rates from every major carrier."),
    REL("freight",   "Freight",   "LTL/FTL for the pallet stuff."),
    REL("claims",    "Claims",    "Auto-filed the moment a claim opens."),
  ],
  cta: commonCta,
};

/* -------- 5. PAYMENTS -------- */
const payments = {
  eyebrow: "Product · Get paid",
  title: "Same-day cash on every invoice.",
  subtitle: "ACH, card, wire, or Verqo Advance — buyers pick, you get paid. One reconciliation, one deposit, zero net-terms drag on your cash cycle. This is the money layer wholesale should have had years ago.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.walletOverview} alt="Verqo Payments — Overview / money flow, wallet, credit" url={URL("/seller/wallet")} />,
  features: {
    heading: "Every payment method your buyers want. One deposit for you.",
    sub: "Bank-grade rails, examiner-ready ledger, and same-day settlement — all embedded in the same portal your buyers already use.",
    items: [
      { icon: Banknote,   title: "ACH & wire",           desc: "Standard rails at bank pricing. Deposits in T+1, wire settlement T+0." },
      { icon: DollarSign, title: "Card acceptance",       desc: "Accept credit card with automatic surcharging — you never eat the interchange." },
      { icon: Bolt,       title: "Verqo Advance",         desc: "Take Net-30/60/90 up front. We fund the invoice within 24 hours, collect from the buyer on their terms, and eat any bad-debt risk." },
      { icon: Receipt,    title: "One reconciliation",    desc: "All rails, one report, straight into QuickBooks, NetSuite, or Xero — with per-line-item attribution." },
      { icon: Wallet,     title: "Multi-currency (soon)",desc: "USD, CAD, EUR, GBP wallets — settle where you want, when you want." },
      { icon: Lock,       title: "SOC 2 + MTL-covered",   desc: "Bank-grade encryption, examiner-ready ledger export, sponsor-bank MTL coverage in all 50 states." },
    ],
  },
  screenshots: {
    heading: "Payments, top to bottom.",
    sub: "Six tabs. One dashboard. Every dollar in and out — wallet, credit, methods, invoices, activity, overview.",
    items: [
      { label: "Overview",  src: IMG.walletOverview, alt: "Payments overview — money in/out, wallet, credit line",     url: URL("/seller/wallet"),          caption: "Overview — money in, money out, wallet balance, credit line utilization. Everything the CFO wants at a glance." },
      { label: "Wallet",    src: IMG.walletWallet,   alt: "Payments — wallet balance & transfers",                     url: URL("/seller/wallet#wallet"),   caption: "Wallet — live balance, one-tap transfer out to your bank, and pending clearance." },
      { label: "Methods",   src: IMG.walletMethods,  alt: "Payments — payout methods (bank accounts)",                  url: URL("/seller/wallet#methods"),  caption: "Methods — connected via Plaid, sandbox-supported for testing. Add cards or additional accounts anytime." },
      { label: "Activity",  src: IMG.walletActivity, alt: "Payments — activity ledger with every deposit and payout",   url: URL("/seller/wallet#activity"), caption: "Activity — every deposit, payout, fee, and refund with full audit trail. Filterable, exportable, examiner-friendly." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "Kill DSO",
      title: "Cash the moment the truck rolls.",
      body: "Verqo Advance funds the invoice within 24 hours — before your buyer even opens the box. We collect from them on their terms.",
      bullets: ["Same-day funding on approved invoices", "Non-recourse (bad debt not your problem)", "Priced per invoice (1.5-3.5%), no minimums"],
      visual: <BrowserFrame src={IMG.walletCredit} alt="Credit line — utilization, limit, next repayment" url={URL("/seller/wallet#credit")} />,
    },
    {
      eyebrow: "Bank-grade infrastructure",
      title: "Rails you can show your CFO.",
      body: "Bank partners, sponsored MTL coverage in 50 states, and examiner-ready ledger export from day one.",
      visual: <BrowserFrame src={IMG.invoicesMain} alt="Invoices — 899 real invoices with terms, amounts, statuses" url={URL("/seller/invoices")} />,
    },
  ],
  quote: {
    quote: "Our DSO dropped from 47 days to 2. We stopped chasing checks and started closing accounts.",
    author: "Priya D.", role: "CFO", company: "a mid-market beauty distributor",
  },
  faq: [
    { q: "What does Verqo Advance cost?",   a: "1.5-3.5% depending on buyer credit — priced per invoice, no minimums, no monthly, no annual." },
    { q: "Is card acceptance surcharged?",   a: "Yes. Buyers who pay by card cover the interchange automatically. You always net 100% of the invoice." },
    { q: "How fast do I get paid?",          a: "ACH clears T+1. Wire settles same-day. Verqo Advance funds within 24 hours of invoice send." },
  ],
  related: [
    REL("credit-terms",  "Credit terms",  "Offer Net-30/60/90 without carrying the risk."),
    REL("auto-invoicing","Auto-invoicing","Invoices the second the truck leaves."),
    REL("buyer-scores",  "Buyer scores",  "Know who to extend terms to."),
  ],
  cta: commonCta,
};

/* -------- 6. CREDIT TERMS (buyer-facing: Net-30/60/90 offered to your buyers) -------- */
const creditTerms = {
  eyebrow: "Product · Get paid",
  title: "Offer Net-30/60/90 to your buyers. Never carry the risk.",
  subtitle: "Say yes to any credit-worthy buyer's terms without carrying a dollar of A/R risk. Every extended-term invoice is underwritten in real time, funded to you on day 1, and insured to the last dollar behind the scenes.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.walletCredit} alt="Verqo Credit — buyer terms, exposure, aging" url={URL("/seller/wallet#credit")} />,
  features: {
    heading: "Offer terms like your biggest competitor. Without their balance sheet.",
    sub: "Every extended-term invoice is underwritten live, funded to you day 1, and insured behind the scenes. You never see a credit application. You never chase a payment.",
    items: [
      { icon: Landmark,     title: "Real limits, real time",       desc: "Every buyer has a live credit limit updated against actual behavior — not a 90-day stale D&B pull." },
      { icon: ShieldCheck,  title: "Non-recourse to you",           desc: "If the buyer defaults, Verqo and Allianz eat it. Your P&L stays clean." },
      { icon: Clock,        title: "Instant approvals at checkout", desc: "Most buyers pre-approved in under 30 seconds at checkout — no forms, no delays, no lost deals." },
      { icon: Gauge,        title: "Portfolio dashboard",           desc: "See term exposure by buyer, region, and channel — with early-warning alerts on aging." },
      { icon: Bell,         title: "Auto-limit adjustments",        desc: "Verqo tightens or loosens buyer credit limits automatically based on payment behavior — you set the rules." },
      { icon: Percent,      title: "Early-pay discounts",           desc: "Offer 2/10 Net-30 automatically to preferred buyers — increase cash velocity without shipping-day discounting." },
      { icon: TrendingUp,   title: "Buyer-specific windows",         desc: "Different terms per buyer, region, tier, or order size — enforced by rules, not by rep memory." },
      { icon: DollarSign,   title: "Day-1 payout on terms",         desc: "You get paid same-day even when your buyer takes Net-60. Verqo funds the gap; buyer pays us on their schedule." },
    ],
  },
  screenshots: {
    heading: "Terms — every open invoice, every aging bucket, one view.",
    sub: "See who's on what terms, where the exposure sits, and when repayments hit — without opening a spreadsheet.",
    items: [
      { label: "Credit line",     src: IMG.walletCredit,   alt: "Credit line dashboard — utilization, buyers, exposure",  url: URL("/seller/wallet#credit"),    caption: "Credit tab — total line, utilization, upcoming maturities, and per-buyer aging in one view. Alerts fire before anything ages past 60 days." },
      { label: "Buyer scoring",   src: IMG.buyersMain,     alt: "Buyer scoring — credit health per buyer",                url: URL("/seller/buyers"),            caption: "Cross-reference buyer credit exposure with live buyer scores — decide who gets more room, who to tighten on." },
      { label: "Activity ledger", src: IMG.walletActivity, alt: "Credit activity ledger",                                 url: URL("/seller/wallet#activity"),  caption: "Every credit event (extension, repayment, extension request) captured with full audit trail." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "The math",
      title: "Same cash flow as pay-now. Same competitive terms as your biggest rival.",
      body: "You get paid day 1. Your buyer pays day 30/60/90. Verqo takes 1.5-3.5% for closing the gap. Everyone wins — except the receivables spreadsheet.",
      visual: <BrowserFrame src={IMG.walletOverview} alt="Payments overview — same-day cash on extended terms" url={URL("/seller/wallet")} />,
    },
    {
      eyebrow: "Allianz-backed",
      title: "Insured to the last dollar.",
      body: "Every extended term is Allianz-insured behind the scenes. Bank-grade cover, no procurement, no renewal calls on your side.",
      visual: <BrowserFrame src={IMG.insuranceMain} alt="Verqo Insurance — coverage in-force on every extended invoice" url={URL("/seller/insurance")} />,
    },
  ],
  faq: [
    { q: "What if my buyer wants Net-90?",       a: "We support Net-30, 60, 90, and custom windows up to 180 days for approved buyers." },
    { q: "Do I control who gets terms?",         a: "Yes. You set the rules (min order, tier, region) — Verqo enforces them at checkout." },
    { q: "What's the underwriting model?",       a: "Live behavioral scoring + cross-network signal + bank data + business KYB — updated every 90 seconds, not every 90 days." },
    { q: "How is this different from seller financing?", a: "This page is about the terms YOU offer YOUR BUYERS (they take Net-30/60/90 from you). Seller financing is a separate product where YOU finance your own outstanding invoices — see /product/seller-financing." },
  ],
  related: [
    REL("seller-financing", "Seller financing", "Finance YOUR outstanding invoices — same-day advance on receivables."),
    REL("payments",         "Payments",         "One reconciliation across every rail."),
    REL("buyer-scores",     "Buyer scores",     "Know the health of every buyer in real time."),
    REL("insurance",        "Insurance",        "Allianz-backed cover, embedded."),
  ],
  cta: commonCta,
};

/* -------- 6b. SELLER FINANCING (Verqo Advance for the seller) -------- */
const sellerFinancing = {
  eyebrow: "Product · Get paid",
  title: "Finance your own invoices. Get paid today, not in 60 days.",
  subtitle: "Verqo Advance is the seller-side of the credit engine: turn any outstanding invoice into same-day cash. Non-recourse, priced per invoice, no lockups, no personal guarantees. This is the financing your growing wholesale business should have had years ago.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.walletOverview} alt="Verqo Advance — same-day advance on outstanding invoices" url={URL("/seller/wallet")} />,
  features: {
    heading: "Financing built for wholesale — priced per invoice, funded same-day.",
    sub: "A working-capital line embedded inside the same portal your invoices live in. No factoring calls, no lockbox, no legacy paperwork.",
    items: [
      { icon: Bolt,          title: "Same-day funding",         desc: "Cash in your Verqo wallet within 24 hours of invoice send — often within hours." },
      { icon: ShieldCheck,   title: "Non-recourse advance",      desc: "If your buyer defaults, Verqo eats it (not you). Your business stays capitalized." },
      { icon: Percent,       title: "Priced per invoice",        desc: "1.5–3.5% per financed invoice, based on the buyer's credit — no monthly, no annual, no minimums." },
      { icon: DollarSign,    title: "No lockbox, no factoring", desc: "Your buyer pays into your Verqo wallet directly. No third-party lockbox, no assigned-receivable notice ruining your buyer relationship." },
      { icon: Landmark,      title: "No personal guarantees",    desc: "Underwritten at the buyer + invoice level, not against your personal credit or a UCC-1 blanket lien." },
      { icon: Gauge,         title: "Draw-when-you-need",        desc: "Only advance the invoices you want. Selective factoring, one invoice at a time, straight from the Payments dashboard." },
      { icon: TrendingUp,    title: "Institutional backing",     desc: "Funded by our institutional lending partner (Capec) — real capital, real limits, real speed." },
      { icon: PieChart,      title: "Live utilization view",     desc: "See advanced balance, in-flight repayments, upcoming maturities, and available headroom in one dashboard." },
    ],
  },
  screenshots: {
    heading: "Financing — inside the wallet you already use.",
    sub: "One tap on an invoice to advance it. One dashboard to see everything in flight. No paperwork, no separate portal.",
    items: [
      { label: "Overview",  src: IMG.walletOverview, alt: "Advance overview — money in, money out, financed",       url: URL("/seller/wallet"),          caption: "Overview — see how much you've advanced, what's coming back, and available headroom next to your regular cash." },
      { label: "Credit",    src: IMG.walletCredit,   alt: "Credit line — advance utilization + upcoming repayments", url: URL("/seller/wallet#credit"),   caption: "Credit tab — advance utilization, maturity schedule, and upcoming repayments in one view." },
      { label: "Activity",  src: IMG.walletActivity, alt: "Activity ledger — advance draws + repayments",           url: URL("/seller/wallet#activity"), caption: "Activity — every advance draw, every repayment, every fee — with full audit trail." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "Growth cash, not survival cash",
      title: "Ship the next PO before this one clears.",
      body: "Most wholesale brands lose 40–70 days of runway to Net-terms. Verqo Advance collapses that gap so you can restock, run ads, and hire — instead of chasing receivables.",
      bullets: ["Fund one invoice or all of them", "No dilution, no equity", "No 12-month lockup"],
      visual: <BrowserFrame src={IMG.invoicesMain} alt="Invoices — one-tap advance on any invoice" url={URL("/seller/invoices")} />,
    },
    {
      eyebrow: "Different from buyer credit terms",
      title: "Two products, one credit engine.",
      body: "Buyer-facing credit terms let YOUR buyers pay you Net-30/60/90. Seller financing (this product) lets YOU turn any outstanding invoice into same-day cash — even invoices that are already on pay-now terms. Use either, or both.",
      cta: { label: "See buyer credit terms", href: "/product/credit-terms" },
      visual: <BrowserFrame src={IMG.walletCredit} alt="Two products, one credit engine — buyer terms vs seller financing" url={URL("/seller/wallet#credit")} />,
    },
  ],
  faq: [
    { q: "How is this different from factoring?",         a: "No lockbox, no assignment notice to your buyer, no long-term contract. Priced per invoice you choose to advance. You can advance one invoice this month and none next month — completely selective." },
    { q: "Is my personal credit checked?",                a: "No. Underwriting happens at the buyer + invoice level. No personal guarantees, no UCC blanket lien on your business assets." },
    { q: "What if my buyer disputes the invoice?",         a: "You get up to 90 days to resolve the dispute. Non-recourse means if the buyer becomes insolvent, we eat it — not you." },
    { q: "How is this different from credit terms?",       a: "Credit terms are what YOU offer YOUR buyers (they get Net-30/60/90). Seller financing is what YOU take on YOUR OWN invoices (you get same-day cash). Two separate products, one engine." },
    { q: "Can I use both?",                                a: "Yes. Many sellers offer buyers Net-60 terms AND also draw an advance on those same invoices — best of both worlds. Verqo handles the ledger cleanly." },
    { q: "Who funds the advance?",                         a: "Our institutional lending partner Capec — real balance-sheet capital, not a P2P pool. Verqo routes, they fund." },
  ],
  related: [
    REL("credit-terms", "Credit terms",       "Offer Net-30/60/90 to your buyers without carrying the risk."),
    REL("payments",     "Payments",           "One reconciliation across every rail."),
    REL("auto-invoicing","Auto-invoicing",    "Invoices generated the second the truck rolls."),
    REL("insurance",    "Insurance",          "Allianz-backed cover on every insured invoice."),
  ],
  cta: commonCta,
};

/* -------- 7. AUTO-INVOICING -------- */
const autoInvoicing = {
  eyebrow: "Product · Get paid",
  title: "Invoices generated the second the truck rolls.",
  subtitle: "Verqo watches your fulfillment events — shipment confirmed, PO signed, milestone hit — and drops the invoice into the buyer's inbox before you can spell \"accounting\".",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.invoicesMain} alt="Verqo Invoices — 899 auto-generated invoices" url={URL("/seller/invoices")} />,
  features: {
    heading: "Zero manual invoicing. Every time.",
    sub: "An event-driven invoicing engine that fires on the events YOU choose — shipment, delivery, milestone, subscription — with every rail and every buyer.",
    items: [
      { icon: Receipt,       title: "Event-triggered",       desc: "Fires on ship confirm, milestone hit, or delivered — you decide the trigger per buyer or per SKU." },
      { icon: Layers,        title: "Multi-line accuracy",   desc: "Pulls prices, MOQs, promos, and adjustments from the source-of-truth catalog — no manual line-item entry." },
      { icon: Bolt,          title: "Instant delivery",      desc: "Buyer receives in email + portal, with pay-now buttons live from the moment of send." },
      { icon: Building2,     title: "GL-ready export",        desc: "Auto-syncs to QuickBooks, NetSuite, Xero, Sage Intacct. Reconciliation happens itself." },
      { icon: FileSpreadsheet,title: "PDF & UBL formats",      desc: "Every invoice available as branded PDF and machine-readable UBL / e-invoice for European buyers." },
      { icon: ShieldCheck,   title: "Audit trail per line",   desc: "Every invoice edit tracked with user, reason, and timestamp — SOC 2 compliant out of the box." },
    ],
  },
  screenshots: {
    heading: "899 invoices, one dashboard.",
    sub: "Every invoice — sent, viewed, paid, disputed — with full audit and one-click drill-down into the source order.",
    items: [
      { label: "All invoices", src: IMG.invoicesMain,   alt: "Invoices dashboard — 899 auto-generated invoices",    url: URL("/seller/invoices"),        caption: "Every invoice, sortable by status (paid / due / overdue / disputed) with pay-status, terms, and buyer info in one row." },
      { label: "Activity",     src: IMG.walletActivity, alt: "Activity — invoice send events + payment attempts",   url: URL("/seller/wallet#activity"), caption: "Activity ledger — every invoice event: sent, opened, paid, disputed. Filter by date, buyer, or invoice." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "For the CFO",
      title: "DSO you can prove, per invoice.",
      body: "Every invoice carries a full audit trail — trigger event, send time, first view, first payment attempt, resolution.",
      visual: <BrowserFrame src={IMG.walletOverview} alt="Cash-flow analytics — DSO trend by invoice cohort" url={URL("/seller/wallet")} />,
    },
  ],
  faq: [
    { q: "Can I edit an invoice after send?", a: "Yes — until the buyer pays. Every change is tracked with reason + user for compliance." },
    { q: "Do you support recurring invoices?", a: "Yes. Set up subscription or milestone billing per buyer or per contract." },
  ],
  related: [
    REL("payments",    "Payments",    "One reconciliation across every rail."),
    REL("credit-terms","Credit terms","Terms embedded in every invoice."),
    REL("catalog",     "Catalog",     "Pricing pulled from your source-of-truth catalog."),
  ],
  cta: commonCta,
};

/* -------- 8. SHIPPING -------- */
const shipping = {
  eyebrow: "Product · Ship",
  title: "Discounted parcel rates. Every carrier. One dashboard.",
  subtitle: "USPS, UPS, FedEx, DHL — buy the cheapest rate that meets the deadline, or let Verqo pick automatically with Smart Rate. Zone-aware, insurance-embedded, and multi-account.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.ordersProg} alt="Verqo Orders — in progress with tracking, carrier, SLA" url={URL("/seller/orders#progress")} />,
  features: {
    heading: "The shipping layer you should have had years ago.",
    items: [
      { icon: Truck,       title: "Multi-carrier rates",  desc: "Every rate from every major carrier, side-by-side — commercial pricing negotiated for you." },
      { icon: Bot,         title: "Smart Rate AI",        desc: "AI picks the best rate against your SLA rules — cheapest, fastest, or best value." },
      { icon: Shield,      title: "Insurance at buy",     desc: "One-click insure per parcel — Allianz-backed at $5K per shipment default." },
      { icon: Layers,      title: "Zone-aware",           desc: "Zone 1-9 shown next to every rate. Never over-pay for out-of-zone shipping again." },
      { icon: Rocket,      title: "Batch labels",         desc: "Print 5,000 labels in one hour. Auto-splits by carrier and service level." },
      { icon: Bell,        title: "Live tracking",         desc: "Auto-scans push tracking updates back to the buyer — no manual look-ups." },
    ],
  },
  screenshots: {
    heading: "Shipping — one screen, every order.",
    sub: "In-transit, delivered, action-needed — every shipment across every carrier in one queue with SLA counters and one-click reprint.",
    items: [
      { label: "All orders",    src: IMG.ordersAll,   alt: "Orders — 910 across all statuses",   url: URL("/seller/orders"),           caption: "All orders — 910 across statuses (paid, awaiting pickup, in transit, delivered) with carrier, tracking, and SLA countdown per row." },
      { label: "Action needed", src: IMG.ordersAction,alt: "Orders — action queue",              url: URL("/seller/orders#action"),    caption: "Action queue — orders needing label print, package, or dispute response. Sorted by SLA proximity, hottest at top." },
      { label: "In progress",   src: IMG.ordersProg,  alt: "Orders — in transit view",            url: URL("/seller/orders#progress"),  caption: "In progress — shipments in-transit with live carrier tracking, ETA, and any exceptions flagged automatically." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "For every ops team",
      title: "Buy 5,000 labels in one hour, without a shipping platform.",
      body: "Batch label generation, per-order shipping rules, and printer-agnostic export. Verqo is thin where you need it and deep where it matters.",
      visual: <BrowserFrame src={IMG.dropshipMain} alt="Fulfillment Center — batch label generation" url={URL("/seller/dropship")} />,
    },
  ],
  faq: [
    { q: "Do I need my own carrier accounts?", a: "No. Verqo Shipping runs on our commercial pricing. Bring your own accounts if you already have them." },
    { q: "Which carriers are supported?",      a: "USPS, UPS, FedEx, DHL, plus 40+ LTL freight carriers and international post partners." },
  ],
  related: [
    REL("freight",  "Freight",  "For the pallet stuff."),
    REL("claims",   "Claims",   "Auto-file damage claims."),
    REL("dropship", "Dropship", "Route retailer orders straight to fulfillment."),
  ],
  cta: commonCta,
};

/* -------- 9. FREIGHT -------- */
const freight = {
  eyebrow: "Product · Ship",
  title: "LTL & FTL freight quotes, side-by-side, in one click.",
  subtitle: "Twelve carriers, one form. Book the cheapest quote and get a BOL emailed to your warehouse in under 60 seconds — with cargo insurance embedded.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.shippingMain} alt="Verqo Shipping — LTL & FTL freight console" url={URL("/seller/shipping")} />,
  features: {
    heading: "Freight, priced like parcel.",
    items: [
      { icon: Truck,       title: "12+ LTL carriers",   desc: "Real quotes from FedEx Freight, YRC, Old Dominion, XPO, ABF, Estes, R+L, and more." },
      { icon: Layers,      title: "Class & density",    desc: "Enter dims + weight — Verqo picks the correct freight class automatically. No NMFC guesswork." },
      { icon: Rocket,      title: "FTL & drayage",      desc: "Broker-priced FTL and drayage moves handled by the same team, same UI." },
      { icon: Shield,      title: "Insured cargo",       desc: "Cargo insurance up to $250K per shipment, embedded — no separate policy." },
      { icon: Bell,        title: "Live tender",         desc: "Real-time carrier acceptance, appointment scheduling, and pickup confirmation." },
      { icon: FileSpreadsheet, title: "BOL & docs",     desc: "BOL, packing lists, and export docs generated instantly — printed at the warehouse in one click." },
    ],
  },
  blocks: [
    {
      eyebrow: "For the pallet stuff",
      title: "Distributor-scale freight, self-serve.",
      body: "Book, tender, and track skids without a phone call — including specialty moves like liftgate, appointment, and inside delivery.",
      visual: <BrowserFrame src={IMG.ordersAll} alt="Orders view — freight shipments alongside parcel" url={URL("/seller/orders")} />,
    },
  ],
  faq: [
    { q: "Can I use my own broker?", a: "Yes — Verqo can quote against your existing broker to keep them honest, or replace them entirely." },
    { q: "How much does it cost?",   a: "No monthly fee. Verqo takes a small margin on top of the carrier rate — always disclosed before you book." },
  ],
  related: [
    REL("shipping","Shipping","For every parcel move."),
    REL("claims",  "Claims",  "Auto-file freight claims."),
    REL("dropship","Dropship","Retailer-direct fulfillment."),
  ],
  cta: commonCta,
};

/* -------- 10. CLAIMS -------- */
const claims = {
  eyebrow: "Product · Ship",
  title: "File carrier claims in 30 seconds. Track them without lifting a finger.",
  subtitle: "Damaged, lost, or missing? Verqo files the claim with the carrier the moment your buyer flags it — and updates you in real time with photos, tracking, and payout status.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.ordersClaims} alt="Verqo Claims — active claims queue" url={URL("/seller/orders#claims")} />,
  features: {
    heading: "The claims desk that runs itself.",
    items: [
      { icon: AlertTriangle, title: "Auto-file on flag",    desc: "Buyer marks damage → claim opens with the carrier automatically. Zero clicks from you." },
      { icon: Layers,        title: "Photo intake",         desc: "Buyer uploads photos in-app. Verqo attaches them to the claim + carrier system." },
      { icon: Clock,         title: "SLA-aware follow-ups", desc: "Carriers get pinged before their SLA lapses. No missed windows, no unpaid claims." },
      { icon: DollarSign,    title: "One-tap payout",       desc: "Approved claims deposit directly to your Verqo wallet in T+3 or faster." },
      { icon: Bot,           title: "AI-drafted appeal",    desc: "If a claim is denied, Verqo drafts an appeal with the right evidence attached — you just approve." },
      { icon: ChartBar,      title: "Recovery reporting",    desc: "Track total recovered vs written-off across every carrier — surface the ones hurting you." },
    ],
  },
  blocks: [
    {
      eyebrow: "1-3% of margin back",
      title: "The average brand under-files claims by 40-60%.",
      body: "Verqo files 100% of eligible events, automatically. For most brands this is $50K-$500K/year of margin they were leaving on the carrier's floor.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Dashboard — recovered claims $$ per quarter" url={URL("/seller")} />,
    },
  ],
  faq: [
    { q: "Which carriers?", a: "USPS, UPS, FedEx, DHL for parcel; every major LTL for freight. Others by request." },
    { q: "What's the cost?", a: "Verqo takes 15% of successfully recovered claims. No monthly, no filing fees, no minimums." },
  ],
  related: [
    REL("shipping",  "Shipping",  "Where every label starts."),
    REL("freight",   "Freight",   "LTL & FTL claims included."),
    REL("insurance", "Insurance", "Pair with insured shipments for full coverage."),
  ],
  cta: commonCta,
};

/* -------- 11. BUYER SCORES -------- */
const buyerScores = {
  eyebrow: "Product · Protect",
  title: "Know who's good for it — before you ship.",
  subtitle: "Every buyer has a Verqo Trade Score, updated live from open invoices, payment behavior, and cross-network signal. Trust the ones you should. Tighten up on the rest.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.buyersMain} alt="Verqo Buyer Scoring — live health score per buyer" url={URL("/seller/buyers")} />,
  features: {
    heading: "A credit bureau for the wholesale channel.",
    sub: "Live, cross-network signal on every buyer — not a stale D&B report from three months ago.",
    items: [
      { icon: Gauge,         title: "Live health score",       desc: "0-100 score updated after every invoice, payment, and dispute — refreshed every 90 seconds." },
      { icon: TrendingUp,    title: "Trend deltas",            desc: "Spot buyers whose score is slipping before they miss a payment." },
      { icon: Building2,     title: "Cross-brand signal",       desc: "Buyer paying late everywhere else? You'll see it first — aggregated, anonymized." },
      { icon: ShieldCheck,   title: "Auto-limit adjustments",   desc: "Verqo tightens or loosens credit limits automatically — you set the rules." },
      { icon: Award,         title: "Portfolio segmentation",   desc: "Group buyers by risk band (A/B/C) and enforce different terms and MOQs per band." },
      { icon: Bell,          title: "Early warning alerts",     desc: "Slack, email, or in-app alerts when a top buyer's score drops below your threshold." },
    ],
  },
  screenshots: {
    heading: "See every buyer's health at a glance.",
    sub: "Score, risk band, terms, exposure, DSO — all in one row per buyer, sortable and filterable.",
    items: [
      { label: "All buyers", src: IMG.buyersMain,   alt: "Buyer scoring — all buyers with score, risk, terms",  url: URL("/seller/buyers"),         caption: "Every buyer with live score, risk band, current terms, and open exposure. Sort by score, exposure, or DSO to find who to tighten on first." },
      { label: "At-risk",    src: IMG.buyersMain,   alt: "At-risk buyers — score dropping",                     url: URL("/seller/buyers?filter=risk"), caption: "At-risk view — buyers whose score has dropped below 60 in the last 30 days. Auto-tighten limits or freeze new orders in one click." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "Not a D&B report",
      title: "Real signal, live, on the buyers you actually sell to.",
      body: "D&B reports every 90 days. Verqo scores update every 90 seconds — from real trade data across the wholesale network.",
      visual: <BrowserFrame src={IMG.walletCredit} alt="Credit exposure — cross-referenced with buyer scores" url={URL("/seller/wallet#credit")} />,
    },
  ],
  faq: [
    { q: "Is my payment data shared?", a: "Aggregated & anonymized signal only. Buyer identities are never shared cross-brand. You benefit from the network, your accounts stay yours." },
  ],
  related: [
    REL("credit-terms", "Credit terms", "Extend terms based on real scores."),
    REL("payments",     "Payments",     "Get paid, however the buyer chooses."),
    REL("insurance",    "Insurance",    "Allianz-backed cover on every insured invoice."),
  ],
  cta: commonCta,
};

/* -------- 12. INSURANCE -------- */
const insurance = {
  eyebrow: "Product · Protect",
  title: "Allianz-backed cover, embedded in every invoice.",
  subtitle: "Every extended-term invoice, every dropship parcel, every high-value shipment — insured against non-payment, damage, and loss. No procurement, no renewals, no separate policy.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.insuranceMain} alt="Verqo Insurance — coverage in-force on every invoice" url={URL("/seller/insurance")} />,
  features: {
    heading: "Bank-grade cover, procurement-free.",
    sub: "Cover activates on the events that trigger it — invoice extended, parcel shipped, dispute opened. No policies to sign, no premiums to track, no renewals to remember.",
    items: [
      { icon: Shield,       title: "Non-payment cover",    desc: "Allianz-backed protection on every extended-term invoice up to $50K default." },
      { icon: Wallet,       title: "Cargo cover",          desc: "Damage, loss, and theft coverage on every insured shipment up to $250K per freight move." },
      { icon: Lock,         title: "Fraud protection",     desc: "ACH fraud, disputed cards, and impersonation — covered." },
      { icon: Clock,        title: "Fast payouts",         desc: "Approved claims paid to your wallet in T+3 or faster." },
      { icon: CircleAlert,  title: "Dispute defense",       desc: "Charge-back disputes handled by our team — you keep 100% of undisputed revenue." },
      { icon: ShieldCheck,  title: "Higher limits on ask",  desc: "Custom limits available for enterprise — up to $500K per invoice." },
    ],
  },
  blocks: [
    {
      eyebrow: "Embedded, not sold",
      title: "You don't buy insurance. You use it.",
      body: "Cover activates on the events that trigger it — invoice extended, parcel shipped, dispute opened. No forms, no premiums, no expiring policies.",
      visual: <BrowserFrame src={IMG.walletCredit} alt="Credit + Insurance — every extended invoice is insured" url={URL("/seller/wallet#credit")} />,
    },
  ],
  faq: [
    { q: "What are the coverage limits?", a: "Standard limits: $50K per invoice, $5K per parcel, $250K per freight shipment. Custom limits available for enterprise." },
    { q: "Do I pay premiums?",             a: "No. Cover is embedded in the fee for the underlying rail (advance, credit line, or shipping)." },
  ],
  related: [
    REL("credit-terms", "Credit terms", "Terms + insurance = risk-free receivables."),
    REL("payments",     "Payments",     "Every rail, one deposit."),
    REL("claims",       "Claims",       "Auto-filed the moment they open."),
  ],
  cta: commonCta,
};

export const SELLER_PRODUCT = {
  catalog, storefront, rfq, dropship,
  payments,
  "credit-terms":     creditTerms,
  "seller-financing": sellerFinancing,
  "auto-invoicing":   autoInvoicing,
  shipping, freight, claims,
  "buyer-scores":     buyerScores,
  insurance,
};
